import { z } from 'zod';
import { AvatarStyleSchema } from './gamification.js';
// ── Role ──────────────────────────────────────────────────────────────────────
export const UserRoleSchema = z.enum([
    'super_admin',
    'tenant_admin',
    'operator',
    'viewer',
    'driver',
    // Phase 2 fleet platform — fleet_admin is scoped to one or more
    // Fleet docs via AccessAssignment.fleet_ids. They see only their
    // attributed fleet(s) in the admin UI and cannot mutate
    // operator-level data (Locations / Chargers / Tariffs / other
    // Fleets). Provisioning is CPO-operator-driven; no auto-creation
    // path in v2.
    'fleet_admin',
    // Grant-tier fix: third_party_admin (the SP / partner templates'
    // system_role) can now ride the TOKEN so a grant-based partner admin
    // resolves with their true tier on the admin surface instead of
    // collapsing to viewer. It is a token/session tier only — User.role
    // persistence still maps it to 'viewer' (mapSystemRoleToUserEnum),
    // and the Mongoose User.role enum deliberately does not include it.
    'third_party_admin',
]);
// ── User ──────────────────────────────────────────────────────────────────────
export const UserSchema = z.object({
    _id: z.string(),
    tenant_id: z.string(),
    /** Optional Location-membership within the parent tenant
     *  (per-Location join codes). Null for users who joined via
     *  Tenant-level codes (multifamily 1:1 case) or who pre-date
     *  the per-Location codes commit. */
    location_id: z.string().nullable().optional(),
    email: z.string().email(),
    name: z.string().min(1),
    role: UserRoleSchema,
    permissions: z.array(z.string()),
    is_active: z.boolean(),
    last_login: z.coerce.date().optional(),
    mfa_enabled: z.boolean(),
    google_id: z.string().optional(),
    apple_id: z.string().optional(),
    stripe_customer_id: z.string().optional(),
    // ── Resident-managed chargers (peer-to-peer sharing) ──────────────
    /** Phone number, E.164 format. Optional for vanilla drivers;
     *  required at the form layer for resident-tier flows. */
    phone: z.string().regex(/^\+[1-9]\d{1,14}$/).nullable().optional(),
    /** Stripe Connect Express account id for residents who host
     *  shared chargers. Null on vanilla drivers; populated when the
     *  resident first toggles "Open to guests" and completes Express
     *  onboarding. */
    stripe_connect_account_id: z.string().nullable().optional(),
    /** True once Express account has charges_enabled AND
     *  payouts_enabled per Stripe webhook. Sharing flows gate on
     *  this. */
    stripe_connect_charges_enabled: z.boolean().default(false),
    /** Disclaimer ack timestamps — separate first-host vs first-on-
     *  someone-else's-pass-list events for legal traceability. */
    first_resident_disclaimer_acked_at: z.coerce.date().nullable().optional(),
    first_pass_list_recipient_disclaimer_acked_at: z.coerce.date().nullable().optional(),
    // rfid_cards removed — see RfidCard collection for card-to-user mapping.
    // ── Gamification fields ───────────────────────────────────────────
    username: z.string().optional(),
    avatar_style: AvatarStyleSchema.optional(),
    avatar_seed: z.string().optional(),
    equipped_skin: z.string().optional(),
    points: z.number().int().nonnegative().default(0),
    total_kwh: z.number().nonnegative().default(0),
    total_sessions: z.number().int().nonnegative().default(0),
    current_rank: z.string().optional(),
    // ── Notification preferences ─────────────────────────────────────
    notify_session_complete: z.boolean().default(true),
    notify_payment_failed: z.boolean().default(true),
    notify_badge_earned: z.boolean().default(true),
    notify_weekly_report: z.boolean().default(true),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
// ── Auth requests / responses ─────────────────────────────────────────────────
export const LoginRequestSchema = z.object({
    email: z.string().email('Enter a valid email'),
    // LOGIN accepts any non-empty password. The 12-character policy applies
    // when a password is SET (register / reset), not when one is verified:
    // the v1->v2 mirror ports v1 bcrypt hashes verbatim, so a mirrored
    // customer's real password may be shorter than 12, and a length gate
    // here locked them out before bcrypt ever ran. max mirrors the API's
    // maxLength so the client never sends what the server rejects.
    password: z.string().min(1, 'Enter your password').max(256),
    /** Which surface the login targets. The admin client sends
     *  'admin', arming the server's role gate (driver accounts are
     *  bounced before any token mints — the same rule magic-link
     *  verify and Google apply to the admin surface). The PWA omits
     *  the field; absent means 'web' and no gate. */
    origin: z.enum(['web', 'admin']).optional(),
});
// Public-safe user fields (no password_hash, mfa_secret, etc.)
//
// `has_payment_method` is a server-derived flag (not on the User doc
// itself) — true iff the driver has both a Stripe customer record AND
// a `default_stripe_payment_method_id` pinned. Same condition the
// sessionStartGate uses to allow / deny "no card on file" at start.
// Surfaced here so the PWA's onboarding card (MapPage) + the Start-
// charging gate modal (PreSessionPage) can read from the auth store
// without an extra fetch on app boot. Optional in the schema for
// backward-compat with consumers built before this field shipped;
// server-side it's always populated.
export const PublicUserSchema = UserSchema.pick({
    _id: true,
    tenant_id: true,
    email: true,
    name: true,
    role: true,
    permissions: true,
}).extend({
    has_payment_method: z.boolean().optional(),
    /**
     * "Can this driver pay by any means" — card OR a wallet balance that
     * clears their OWN tenant's minimum. Distinct from has_payment_method
     * above, which is strictly "holds a card".
     *
     * ADVISORY. Emitted only by /auth/me, so it is ABSENT from every
     * login-style payload; consumers must treat absent as "unknown" and
     * test `!== false`. It cannot account for another operator's minimum
     * balance, fleet sponsorship, a cross-tenant guest session, or a
     * zero-rate charger — see hasAnyFundingSource in @evi/billing. Never
     * gate a Start button on it; that reads the per-charger
     * start-preview verdict.
     */
    has_funding_source: z.boolean().optional(),
    // Fleet attribution (Phase 1 fleet platform). Both fields null
    // for personal-billed drivers (the common case); set together
    // when the driver belongs to a Fleet. Used by the PWA's Profile
    // page ("Fleet member: {fleet_name}") and as a hint for whether
    // to render the "Billed to {Fleet Name}" pill on session views
    // that haven't fetched fleet_name via the session response yet.
    fleet_id: z.string().nullable().optional(),
    fleet_name: z.string().nullable().optional(),
    // Fleet Mode Phase 3 — driver-shell selector exposed to the PWA.
    // Phase 1 added User.current_mode at the schema layer; Phase 3
    // surfaces it on /auth/me so the PWA can render the FLEET / PUBLIC
    // pill, the Profile mode-toggle row, and gate session-create UI
    // off a single source of truth without an extra fetch on boot.
    current_mode: z.enum(['fleet', 'public']).optional(),
    // Phase 7g.4a — Sales Partner discriminator. True iff the user
    // has a UserRoleAssignment to a Role doc whose template ===
    // 'sales_agent'. Resolved server-side at login/refresh and
    // surfaced on the wire so the admin client can flip nav +
    // dashboard rendering without a follow-up role lookup.
    // Optional for backward compatibility with non-admin shells
    // that don't need to discriminate (driver PWA, etc.); admin
    // shell treats absence as false.
    is_sales_agent: z.boolean().optional(),
    // Phase 7g.4b.1 — Sales Partner's bound Agent._id, populated at
    // login when is_sales_agent is true. Surfaces in the LoginResponse
    // so the admin client can navigate to /agents/:agent_id (the SP
    // payouts view) and POST to /admin/agents/:agent_id/connect/start
    // without a follow-up listAgents() round-trip. Null/absent for
    // non-SP users.
    agent_id: z.string().optional(),
    // Building Admin Reports commit 7 — Role.template surfaced on the
    // wire so the admin client can differentiate building_admin /
    // property_manager / charger_owner / sales_agent from a plain
    // tenant_admin system role. Drives nav visibility
    // (visibleFor: ['building_admin']), App.tsx route branching, and
    // PersonaColumn.visibleFor gating. Null for system roles
    // (super_admin / tenant_admin / operator / viewer / driver /
    // fleet_admin all have Role.template === null at the data layer)
    // and for legacy users without a UserRoleAssignment. is_sales_agent
    // remains alongside for back-compat — both wire fields read from
    // the same Role.template value but stay independent so a legacy
    // client that only consumes is_sales_agent keeps working.
    template: z.enum([
        'sales_rep',
        'sales_manager',
        'supplier_account',
        'building_admin',
        'charger_owner',
        'property_manager',
        'sales_agent',
        'sales_partner_member',
    ]).nullable().optional(),
    // Driver Themes ticket — server-canonical theme family + mode.
    // Promotes family selection from localStorage to the server so the
    // choice persists across devices. ThemeProvider on the PWA reads
    // through /auth/me to seed in-memory state. Optional in the schema
    // so non-driver shells (admin) that don't need to read the field
    // can omit it from their /auth/me response without breaking
    // PublicUser deserialization; the driver PWA always expects both.
    active_theme_family: z.enum([
        'default', 'luce', 'cartographic',
        'neon', 'electric-light', 'dark-electric', 'obsidian',
        // Post unlock-model rewrite — milestone-locked theme families.
        // Lockstep with the User.active_theme_family Mongoose enum at
        // packages/db/src/models/User.ts and the ThemeFamilyId union
        // at packages/skins/src/contract.ts.
        'aurora',
        'cyber',
    ]).optional(),
    theme_mode: z.enum(['light', 'dark']).optional(),
    // EVIE guided tour completion. Null/absent = never ran (the PWA's
    // first-launch auto-open fires). Optional so non-driver shells that don't
    // read it can omit it from their /auth/me response.
    tour_completed_at: z.coerce.date().nullable().optional(),
    // Hidden arcade unlock. Null/absent = locked (no menu entry, /arcade
    // bounces). Optional so shells that don't surface it can omit it.
    arcade_unlocked_at: z.coerce.date().nullable().optional(),
    // Notification-permission ask. Null/absent = never declined, so the
    // post-install prompt may appear. Set = the driver said "don't ask
    // again" on SOME device; server-side precisely because browser
    // permission state does not sync across devices.
    push_prompt_dismissed_at: z.coerce.date().nullable().optional(),
    // Home-screen shortcuts (frictionless flow, piece 1). The driver's own
    // three pins, or null/absent = the persona defaults. Persona and the
    // home charger id are derived server-side on /auth/me so the PWA can
    // resolve every pin's target without a second fetch. Optional so shells
    // that don't surface them can omit them.
    pinned_shortcuts: z.array(z.string()).nullable().optional(),
    shortcut_persona: z.enum(['homeowner', 'fleet', 'driver']).optional(),
    home_charger_id: z.string().nullable().optional(),
    // A building staff login (Warren, 28 Oct 2026, item 5): invited from a
    // location page, it sees that location's chargers, live status and alerts
    // only. /auth/me sends true for such a login and omits the field
    // otherwise; the admin shows them the building staff menu.
    facilities_only: z.boolean().optional(),
    // A supplier login (lib/supplierBoundary.ts): the supplier template or the
    // factory flag. Sent as true or omitted; the admin shows the supplier pages
    // and opens no live stream.
    supplier_only: z.boolean().optional(),
    // Is this login EVI staff (apps/api lib/eviStaff.ts; Warren, 4 Nov 2026)? The
    // Admin shows the Alerts menu item on this flag and never on a persona.
    // /auth/me and both MFA completions send it; absent reads as false.
    can_see_charger_alerts: z.boolean().optional(),
});
export const LoginResponseSchema = z.object({
    access_token: z.string(),
    user: PublicUserSchema,
});
export const AuthTokenPayloadSchema = z.object({
    sub: z.string(),
    tenant_id: z.string(),
    role: UserRoleSchema,
    permissions: z.array(z.string()),
    iat: z.number(),
    exp: z.number(),
});
// ── Invite / update user ──────────────────────────────────────────────────────
export const InviteUserSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1),
    role: UserRoleSchema,
});
export const UpdateUserSchema = z.object({
    name: z.string().min(1).optional(),
    role: UserRoleSchema.optional(),
    permissions: z.array(z.string()).optional(),
    is_active: z.boolean().optional(),
});
//# sourceMappingURL=user.js.map